import React, { useEffect, useState, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { Amplify } from "aws-amplify";
import { generateClient } from "aws-amplify/api";
import { Drawer, message } from "antd";

import config from "../../aws-exports";

import {
  ProfileAction,
  MessageAction,
  SubscriptionAction,
  NotificationAction,
} from "../../store/actions";

import UnderHeaderMenu from "../../components/underheadermenu";
import NavHeader from "../../components/layout/dashboard/navheader";

import useScreenSize from "../../utils/usescreensize";
import getImageName from "../../utils/getImageName";

import Messages from "./messages";
import MessagesSidebar from "./messagessidebar";
import MessageHeader from "./messageheader";
import MessageSendButton from "./messagesendbutton";

import { listMessagesForRoom, listRooms } from "../../graphql/queries";
import { createRoom } from "../../graphql/mutations";
import {
  onCreateMessageByRoomId,
  onMessageStatusUpdate,
} from "../../graphql/subscriptions";

import images from "../../assets";

import { APPSYNC_API_KEY } from "../../config/constants";

import "./styles.css";

Amplify.configure(config);

const apiKey = APPSYNC_API_KEY;

const client = generateClient();

const MessagesLayout = () => {
  const dispatch = useDispatch();
  const screenSize = useScreenSize();
  const profileDataRef = useRef(null);

  const user = useSelector((state) => state.auth?.user);
  const profileData = useSelector((state) => state?.profile?.profile);

  const { isLoading } = useSelector((state) => state?.message);
  const [userList, setUserList] = useState([]);

  const [searchParams, setSearchParams] = useSearchParams();
  const [state, setState] = useState({
    tab: "All",
    activeUser: {},
    search: "",
  });

  const [filteredUserList, setFilteredUserList] = useState([]);

  const [lastMessage, setLastMessage] = useState(null);
  const [messages, setMessages] = useState([]);
  const [messagesLoading, setMessagesLoading] = useState();
  const [open, setOpen] = useState(true);

  useEffect(() => {
    if (!profileData?.username) {
      dispatch(
        ProfileAction.getProfile((res) => {
          if (res?.activePlan) {
            dispatch(
              SubscriptionAction.getSubscriptionInfo({
                transactionId: res?.activePlan?.transactionId,
              })
            );
          }
        })
      );
    }
  }, [user]);

  useEffect(() => {
    if (profileData?.username) {
      profileDataRef.current = profileData;
      dispatch(NotificationAction.getNotifications());
    }
  }, [profileData]);

  useEffect(() => {
    let unreadCount = 0;
    userList?.map((user) => {
      if (
        user.lastSenderId !== profileData.username &&
        user.lastMessageRead === false
      ) {
        unreadCount++;
      }
    });
    dispatch(MessageAction.setMessageSidebarCount(unreadCount));
  }, [userList]);

  useEffect(() => {
    if (profileData?.username) {
      const payload = {
        userList: userList,
        isLoading: false,
      };
      dispatch(MessageAction.setMessageList(payload));
    }
  }, [userList]);

  useEffect(() => {
    if (state?.search) {
      const updatedList = userList.filter(
        (user) =>
          user?.receiverName
            ?.toLowerCase()
            ?.includes(state?.search?.toLowerCase()) ||
          user?.senderName
            ?.toLowerCase()
            ?.includes(state?.search?.toLowerCase())
      );

      setFilteredUserList(updatedList);
    } else {
      setFilteredUserList(userList);
    }
  }, [state?.search, userList]);

  useEffect(() => {
    if (profileData?.username && state?.activeUser) {
      getMessagesOfRoom(true);
    }
  }, [profileData, state?.activeUser]);

  useEffect(() => {
    if (profileData?.username) {
      getRoomList(true, profileData?.username);
    }
    if (
      profileData?.username &&
      searchParams.get("username") &&
      !isNull(searchParams.get("username")) &&
      searchParams.get("firstName") &&
      !isNull(searchParams.get("firstName")) &&
      searchParams.get("lastName") &&
      !isNull(searchParams.get("lastName"))
    ) {
      const username = searchParams.get("username");
      const firstName = searchParams.get("firstName");
      const lastName = searchParams.get("lastName");
      const photo = searchParams.get("photo")
        ? getImageName(searchParams.get("photo"))
        : "";

      createSingleRoom(username, firstName, lastName, photo);
    }
  }, [profileData]);

  useEffect(() => {
    const msgsubscription = client
      .graphql({
        query: onCreateMessageByRoomId,
        variables: {},
      })
      .subscribe({
        next: (data) => {
          const currentProfile = profileDataRef.current;
          const message =
            data?.data?.onCreateMessageByRoomId?.message?.content?.sender ===
            currentProfile?.username
              ? data?.data?.onCreateMessageByRoomId?.messageForSender
              : data?.data?.onCreateMessageByRoomId?.messageForReceiver;

          setMessages((prevMsgs) => [...prevMsgs, message]);
          setLastMessage(message);
          getRoomList(false, currentProfile?.username);
        },
      });
    return () => msgsubscription.unsubscribe();
  }, []);

  useEffect(() => {
    const statusUpdateSubscription = client
      .graphql({
        query: onMessageStatusUpdate,
        variables: {},
      })
      .subscribe({
        next: async () => {
          await getMessagesOfRoom(false);
        },
      });
    return () => statusUpdateSubscription.unsubscribe();
  }, [profileData, state?.activeUser]);

  function isNull(text) {
    if (!text || text === "null" || text === "undefined") {
      return true;
    } else {
      return false;
    }
  }

  const getRoomList = async (
    loading = false,
    username = profileData?.username
  ) => {
    try {
      const payload = {
        isLoading: loading,
      };
      dispatch(
        MessageAction.setMessageList(payload, async () => {
          const result = await client.graphql({
            query: listRooms,
            variables: {
              searchId: username,
            },
          });
          setUserList(result?.data?.listRooms?.items);
        })
      );
    } catch (e) {
      console.error("in get Room list error", e);
    }
  };

  const getMessagesOfRoom = async (showLoading) => {
    try {
      if (state?.activeUser?.id) {
        setMessagesLoading(showLoading);
        const result = await client.graphql({
          query: listMessagesForRoom,
          variables: {
            roomId: state?.activeUser?.id,
            username: profileData?.username,
          },
          headers: {
            "x-api-key": apiKey,
          },
        });
        setMessagesLoading(false);
        const fetchedMessages = result?.data?.listMessagesForRoom?.items || [];
        setMessages(fetchedMessages);
        setLastMessage(fetchedMessages[fetchedMessages.length - 1] || null);
        getRoomList(false, profileData?.username);
      }
    } catch (e) {
      setMessagesLoading(false);
    }
  };

  const createSingleRoom = async (
    receiverUsername,
    firstName,
    lastName,
    photo
  ) => {
    try {
      const result = await client.graphql({
        query: createRoom,
        variables: {
          input: {
            senderId: profileData?.username,
            receiverId: receiverUsername,
            senderName: `${profileData?.firstName} ${profileData?.lastName}`,
            senderImageUrl: profileData?.profilePicture
              ? getImageName(profileData?.profilePicture)
              : "",
            receiverName: `${firstName} ${lastName}`,
            receiverImageUrl: photo,
          },
        },
        headers: {
          "x-api-key": apiKey,
        },
      });

      if (!result || !result?.data?.createRoom) {
        message.error("Something went wrong");
      } else {
        setState({ ...state, activeUser: result?.data?.createRoom });
      }
    } catch (e) {
      console.error("in create room error", e);
    }
  };

  return (
    <>
      <div className="messages-layout">
        {screenSize.width > 1023 && (
          <MessagesSidebar
            state={state}
            setState={setState}
            userList={filteredUserList}
            userListLoading={isLoading}
          />
        )}
        <div className="messages-right-side">
          {screenSize.width > 1023 ? (
            <NavHeader />
          ) : (
            !state?.activeUser?.id && open && <NavHeader />
          )}

          {!state?.activeUser?.id && screenSize.width < 1023 && (
            <UnderHeaderMenu titlename="Messages" />
          )}

          {screenSize.width < 1023 && !state?.activeUser?.id && (
            <Drawer
              className="messages-drawer"
              closable={false}
              mask={false}
              placement="left"
              width="100%"
              open={open}
            >
              <MessagesSidebar
                state={state}
                setState={setState}
                userList={filteredUserList}
                userListLoading={isLoading}
              />
            </Drawer>
          )}

          {!state?.activeUser?.id ? (
            <div className="flex flex-col justify-center items-center p-10 w-full gap-4 bg-white h-full m-3 rounded-xl">
              <img
                src={images.noMessageIcon}
                alt="no notifications"
                className="h-[160px] lg:h-[220px]"
              />
              <div className="text-basePrimary font-bold text-[24px] lg:text-[32px]">
                Select a conversation
              </div>
              <p className="text-[14px] text-center max-w-[500px]">
                When you start chatting, you’ll see the full conversation here.
              </p>
            </div>
          ) : (
            <>
              <MessageHeader
                activeUser={state?.activeUser}
                state={state}
                setState={setState}
              />
              <div className="messages-content">
                <Messages
                  state={state}
                  setState={setState}
                  messages={messages}
                  setMessages={setMessages}
                  activeUser={state?.activeUser}
                  messagesLoading={messagesLoading}
                />
              </div>
              <div className="message-bottom-bar">
                <MessageSendButton
                  messages={messages}
                  setMessages={setMessages}
                  activeUser={state?.activeUser}
                  apiKey={apiKey}
                  client={client}
                  lastMsgObj={lastMessage}
                />
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default MessagesLayout;
